import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useOktaAuth } from '@okta/okta-react';
import { useLogoutContext } from './useLogOutConfig';
import { ExtraHeaders } from '../query/useApiHeaders';
import { useApiGet } from '../query/api-hooks';
import { useIsTokenExpired } from '../auth/user-info.hooks';

type ObjectMap = {
  [key: string]: any;
};
export type CmsExtraQueryOptions = {
  /**Set to `false` if you do not want api to fetch in logout mode.
   */
  isLogoutApi?: boolean;

  /**Set to `false` if you do not want to modify the UserLoggedIn query param.
   */
  modifyUserLoggedInParam?: boolean;
};

const DEFAULT_404_RESPONSE: ObjectMap = { hasNoContent: true };
const DEFAULT_EXTRA_OPTIONS: CmsExtraQueryOptions = {
  isLogoutApi: true,
  modifyUserLoggedInParam: true,
};

/** Customized implementation of `useApiQuery` from mf_core/query to handle only CMS api calls for both login/logout.
 * @description This does not return queryResult's `isError`=`true` in case of API 404.
 * To custom handle 404 API errors use `data.hasNoContent` boolean value instead.
 * To use api for login only api send extraQueryOptions param as { isLogoutApi: false }
 * @param apiRoute api endpoint eg: `"/cms/v1/resources"`
 * @param queryParams `UserLoggedIn: boolean` gets addded as a query param for all queries
 * @param useQueryOptions react-query UseQueryOptions
 * @param extraHeaders Adds extra header as object type {[x: string]: string | undefined}
 * @param extraQueryOptions  Default is { isLogoutApi: true, modifyUserLoggedInParam: true }
 */
export function useApiQueryForCms<TO = ObjectMap>(
  apiRoute: string,
  queryParams?: ObjectMap,
  useQueryOptions?: Partial<UseQueryOptions<TO>>,
  extraHeaders?: ExtraHeaders,
  extraQueryOptions = DEFAULT_EXTRA_OPTIONS
) {
  const oktaContext = useOktaAuth();
  const cmsData = useLogoutContext();
  const isTokenExpired = useIsTokenExpired();
  const isAuthenticated = oktaContext?.authState?.isAuthenticated;
  const customOptions = {
    isLogoutApi: Boolean(
      !isAuthenticated &&
        extraQueryOptions?.isLogoutApi &&
        cmsData?.data?.isLogoutEnabled
    ),
  };
  const apiGet = useApiGet<TO>(customOptions);
  const route = isAuthenticated
    ? apiRoute
    : apiRoute.replace('/cms/v1', '/cms-noauth/v1');
  let params = queryParams;
  if (extraQueryOptions?.modifyUserLoggedInParam) {
    params = {
      ...queryParams,
      UserLoggedIn: queryParams?.UserLoggedIn ?? isAuthenticated,
    };
  }

  const enabled = Boolean(
    ((!isTokenExpired && isAuthenticated) || customOptions?.isLogoutApi) &&
      (useQueryOptions?.enabled || useQueryOptions?.enabled === undefined)
  );

  return useQuery<TO>({
    ...useQueryOptions,
    enabled: enabled,
    queryFn: () =>
      apiGet(route, params, extraHeaders).catch((error) => {
        if (error?.response?.status === 404) {
          return DEFAULT_404_RESPONSE as unknown as TO;
        }
        throw error;
      }),
    queryKey: [route, params, extraHeaders],
    retry: 0,
  });
}
